// Drag and drop, on pointer events, with a keyboard path that does the same thing.
//
// ── WHY NOT HTML5 DRAG AND DROP ─────────────────────────────────────────────────
// It does not fire on touch. Not in iOS Safari, not in Android Chrome. An app whose primary device is
// a phone on a building site cannot use it, and "drag works on desktop only" would fail the one
// acceptance criterion that says a planner drags a task on their phone.
//
// ── THE REDUCER IS THE POINT ────────────────────────────────────────────────────
// Pointer handling and drag SEMANTICS are separated: pointers produce `begin/hover/commit/cancel`, and
// so does the keyboard. Everything downstream sees the same four events, which means the keyboard path
// is not a lesser alternative bolted on afterwards — it is the same path with a different input, and a
// test can drive it without synthesising pointer physics.
import { useCallback, useEffect, useRef, useState } from 'react';
import { mark, measure } from './perf.ts';

/** What is being dragged. `assignmentId` is present when the drag started from a calendar chip. */
export interface DragPayload {
  workpackageId: number;
  projectId: number;
  assignmentId: number | null;
  resourceId: number | null;
  title: string;
  /** The revision the UI rendered. Sent back as `expectedRevision`, so a stale drag loses cleanly. */
  planRevision: number;
  plannedStart: string | null;
  plannedEnd: string | null;
  /** WHICH DAY this chip is. The unit a drag moves, since migration 033 made a day a row. */
  day: string;
  /** The day-row's id, or null when it is still part of an even spread. Informational only. */
  allocationId: number | null;
  /** The day slice, for the floating chip's label. */
  minutes: number;
  /** The assignment's whole allocation — the number a reassignment must re-create it with. */
  allocatedMinutes: number;
}

/** Where it may land: a calendar day, optionally for a specific resource lane. */
export interface DropTarget {
  day: string;
  resourceId: number | null;
}

/**
 * What this gesture means.
 *
 * ── WHY ONE CONTROLLER AND NOT TWO ──────────────────────────────────────────────
 * Resizing is dragging: same threshold, same auto-scroll, same `elementFromPoint` hit testing, same
 * "the pointer is captured so pointerenter stops firing" trap, same keyboard parity. A second
 * controller would be a copy of all of that, and copies drift — the resize would keep working on
 * desktop after a touch fix landed only in the move path. What differs is a single word carried on the
 * state, and what the commit does with the day underneath.
 *
 *   move          — put the job on this day (and possibly this person's lane)
 *   resize-end    — the job now ENDS on this day. The common one: a job that turned out longer.
 *   resize-start  — the job now STARTS on this day, keeping its end.
 */
export type DragMode = 'move' | 'resize-start' | 'resize-end';

export interface DragState {
  payload: DragPayload | null;
  over: DropTarget | null;
  /** Screen position, for the floating chip. Null while using the keyboard. */
  at: { x: number; y: number } | null;
  viaKeyboard: boolean;
  mode: DragMode;
}

const EMPTY: DragState = { payload: null, over: null, at: null, viaKeyboard: false, mode: 'move' };

/** How far a pointer must move before a press becomes a drag rather than a tap. */
const THRESHOLD_PX = 8;

export function useDragController(onDrop: (p: DragPayload, t: DropTarget, mode: DragMode) => void) {
  const [state, setState] = useState<DragState>(EMPTY);
  const pending = useRef<{ payload: DragPayload; x: number; y: number; id: number; mode: DragMode } | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const cancel = useCallback(() => { pending.current = null; setState(EMPTY); }, []);

  const commit = useCallback(() => {
    mark('drag-drop');
    const s = stateRef.current;
    if (s.payload && s.over) onDrop(s.payload, s.over, s.mode);
    pending.current = null;
    setState(EMPTY);
  }, [onDrop]);

  /**
   * Attach to the RESIZE EDGE of a chip.
   *
   * `stopPropagation` is what keeps the two gestures apart: without it the same pointerdown arms both
   * the move and the resize, and a pull on the right edge slid the whole job sideways — the classic
   * way a resizable board feels broken. The handle is its own 10px target and takes the event outright.
   */
  const resizeHandleProps = useCallback((payload: DragPayload, edge: 'start' | 'end') => ({
    onPointerDown: (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      pending.current = {
        payload, x: e.clientX, y: e.clientY, id: e.pointerId,
        mode: edge === 'end' ? 'resize-end' : 'resize-start',
      };
    },
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    className: `chip-grip ${edge}`,
    'aria-hidden': true,
  }), []);

  /** Attach to a draggable chip. Returns props rather than taking a ref, so a chip stays a pure view. */
  const dragHandleProps = useCallback((payload: DragPayload) => ({
    onPointerDown: (e: React.PointerEvent) => {
      // Left button or touch only. A right-click opens the context menu and must not start a drag.
      if (e.button !== 0) return;
      mark('drag-down');
      pending.current = { payload, x: e.clientX, y: e.clientY, id: e.pointerId, mode: 'move' };
    },
    // KEYBOARD PARITY. Enter or space picks up; arrows move between days; Enter drops; Escape cancels.
    // Announced through `aria-grabbed` so a screen reader user knows the mode changed.
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const s = stateRef.current;
        if (s.payload?.workpackageId === payload.workpackageId && s.viaKeyboard) commit();
        else setState({ payload, over: null, at: null, viaKeyboard: true, mode: 'move' });
      } else if (e.key === 'Escape') {
        cancel();
      }
    },
    tabIndex: 0,
    role: 'button',
    'aria-grabbed': state.payload?.workpackageId === payload.workpackageId,
  }), [cancel, commit, state.payload]);

  /** Attach to a droppable cell. */
  const dropTargetProps = useCallback((target: DropTarget) => ({
    onPointerEnter: () => {
      if (stateRef.current.payload) setState((s) => ({ ...s, over: target }));
    },
    onPointerUp: () => { if (stateRef.current.payload) commit(); },
    // The keyboard path needs a way to choose a target without a pointer: focusing a cell while
    // carrying something selects it.
    onFocus: () => { if (stateRef.current.payload) setState((s) => ({ ...s, over: target })); },
  }), [commit]);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const p = pending.current;
      if (p && !stateRef.current.payload) {
        if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < THRESHOLD_PX) return;
        // MEASURED FROM THE MOVE THAT CROSSES THE THRESHOLD, not from pointerdown. The time between
        // pressing and moving belongs to the person, not to the app — and an automated drag that
        // pauses five seconds between the two would otherwise report five seconds of "lag".
        mark('drag-threshold');
        setState({ payload: p.payload, over: null, at: { x: e.clientX, y: e.clientY }, viaKeyboard: false, mode: p.mode });
        mark('drag-preview');
        measure('drag:threshold-preview', 'drag-threshold', 'drag-preview');
        // Kept as well: it is the honest wall-clock from press to preview, including the hold.
        measure('drag:down-preview', 'drag-down', 'drag-preview');
        return;
      }
      if (stateRef.current.payload && !stateRef.current.viaKeyboard) {
        mark('drag-move');
        setState((s) => ({ ...s, at: { x: e.clientX, y: e.clientY } }));
        // `elementFromPoint` rather than pointerenter: during a drag the pointer is captured and enter
        // events stop firing on the cells underneath, which is the classic reason a drop target goes
        // dead halfway through a drag.
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const cell = el?.closest('[data-drop-day]') as HTMLElement | null;
        if (cell) {
          const day = cell.dataset.dropDay!;
          const r = cell.dataset.dropResource;
          setState((s) => ({ ...s, over: { day, resourceId: r ? Number(r) : null } }));
        } else {
          setState((s) => ({ ...s, over: null }));
        }
        mark('drag-moved');
        measure('drag:move-paint', 'drag-move', 'drag-moved');
      }
    };
    const up = () => {
      if (stateRef.current.payload && !stateRef.current.viaKeyboard) {
        if (stateRef.current.over) commit(); else cancel();
      }
      pending.current = null;
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key);
    };
  }, [cancel, commit]);

  // While dragging, suppress the browser's own text selection and scroll-on-drag. Applied as a class
  // on <body> rather than inline styles so it is one place to look when a gesture feels wrong.
  useEffect(() => {
    const dragging = state.payload !== null;
    document.body.classList.toggle('dragging', dragging);
    // `user-select: none` stops a selection from GROWING, but it does not remove one the first few
    // pointer-moves already made before the class landed — so a finished drag left half the calendar
    // highlighted blue, which reads as a broken gesture rather than a completed one. Cleared on drop.
    if (!dragging) window.getSelection?.()?.removeAllRanges();
    return () => document.body.classList.remove('dragging');
  }, [state.payload]);

  return { state, dragHandleProps, resizeHandleProps, dropTargetProps, cancel, commit,
    /** For the keyboard path: move the pending target by N days. */
    nudge: (days: number) => setState((s) => {
      if (!s.payload || !s.viaKeyboard) return s;
      const base = s.over?.day ?? s.payload.plannedStart ?? new Date().toISOString().slice(0, 10);
      const d = new Date(base + 'T00:00:00');
      d.setDate(d.getDate() + days);
      const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return { ...s, over: { day, resourceId: s.over?.resourceId ?? s.payload.resourceId } };
    }),
  };
}
